import React, { useState, useEffect, useMemo } from "react";
import {
  ArrowBack,
  Assessment,
  CalendarToday,
  TrendingUp,
  Receipt,
  ViewModule,
} from "@material-ui/icons";
import { useHistory } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { Chart } from "react-google-charts";
import axios from "axios";
import fetchClient from "../../api/fetchClient";
import Loader from "../../components/Loader";
import message from "../../helper/message";
import { formatAbbrValue } from "../../helper/formatValue";
import {
  Container,
  ModernHeader,
  HeaderLeft,
  HeaderContent,
  PageTitle,
  PageSubtitle,
  ResultsCard,
} from "../../components/common";

const E1_API_URL = process.env.REACT_APP_E1_API_PATH;
const E1_API_KEY = process.env.REACT_APP_E1_API_KEY;

const MONTH_NAMES = [
  "Jan", "Fev", "Mar", "Abr", "Mai", "Jun",
  "Jul", "Ago", "Set", "Out", "Nov", "Dez",
];

const SOURCE_COLORS = {
  EssentialOne: "#3498db",
  Comerzzia: "#00bcd4",
  Concur: "#8e44ad",
  Viceri: "#667eea",
  Ariba: "#e67e22",
  Senior: "#84cc16",
};

const PIE_COLORS = ["#3498db", "#00bcd4", "#8e44ad", "#667eea", "#e67e22", "#84cc16"];

function DashboardConsolidado() {
  const history = useHistory();
  const now = new Date();

  const [isLoading, setIsLoading] = useState(false);
  const [sourceData, setSourceData] = useState(null);
  const [year, setYear] = useState(now.getFullYear());
  const [selectedModules, setSelectedModules] = useState(["EssentialOne", "Comerzzia", "Concur", "Viceri", "Ariba", "Senior"]);

  const ALL_MODULES = ["EssentialOne", "Comerzzia", "Concur", "Viceri", "Ariba", "Senior"];

  const toggleModule = (mod) => {
    setSelectedModules((prev) => {
      if (prev.includes(mod)) {
        if (prev.length === 1) return prev;
        return prev.filter((m) => m !== mod);
      }
      return [...prev, mod];
    });
  };

  const toggleAll = () => {
    setSelectedModules((prev) =>
      prev.length === ALL_MODULES.length ? [ALL_MODULES[0]] : [...ALL_MODULES]
    );
  };

  const yearOptions = [];
  for (let y = now.getFullYear(); y >= 2020; y--) {
    yearOptions.push(y);
  }

  useEffect(() => {
    fetchAllData();
  }, [year]);

  const fetchAllData = async () => {
    setIsLoading(true);
    const results = { EssentialOne: [], Comerzzia: [], Concur: [], Viceri: [], Ariba: [], Senior: [] };

    try {
      const settled = await Promise.allSettled([
        fetchE1Data(year),
        fetchComerzziaData(year),
        fetchConcurData(year),
        fetchViceriData(year),
        fetchAribaData(year),
        fetchSeniorData(year),
      ]);

      if (settled[0].status === "fulfilled") results.EssentialOne = settled[0].value;
      if (settled[1].status === "fulfilled") results.Comerzzia = settled[1].value;
      if (settled[2].status === "fulfilled") results.Concur = settled[2].value;
      if (settled[3].status === "fulfilled") results.Viceri = settled[3].value;
      if (settled[4].status === "fulfilled") results.Ariba = settled[4].value;
      if (settled[5].status === "fulfilled") results.Senior = settled[5].value;

      setSourceData(results);
    } catch (error) {
      console.error("Erro ao buscar dados consolidados", error);
      message.error("Erro ao buscar dados consolidados");
    } finally {
      setIsLoading(false);
    }
  };

  // Ariba - Internal API (valores apenas BRL)
  const fetchAribaData = async (yr) => {
    const { data: grps } = await fetchClient.get("/ariba/getAllGroups");
    const allMetrics = [];
    for (const grp of (grps || [])) {
      try {
        const { data } = await fetchClient.get(`/ariba/metrics?grupoId=${grp.id}&year=${yr}`);
        allMetrics.push(...(data || []));
      } catch (e) {
        console.log(`Ariba grupo ${grp.id} sem dados para ${yr}`);
      }
    }
    const grouped = {};
    allMetrics.forEach((metric) => {
      const m = parseInt(metric.month);
      if (!grouped[m]) grouped[m] = { docCount: 0, totalValue: 0 };
      grouped[m].docCount += metric.transactionCount || 0;
      if (!metric.currency || metric.currency === "BRL")
        grouped[m].totalValue += metric.totalValue || 0;
    });
    return Object.entries(grouped).map(([month, d]) => ({
      source: "Ariba", month: parseInt(month), docCount: d.docCount, totalValue: d.totalValue,
    }));
  };

  // Senior WMS - Internal API (valores apenas BRL)
  const fetchSeniorData = async (yr) => {
    const { data: grps } = await fetchClient.get("/wms/getAllGroups");
    const allMetrics = [];
    for (const grp of (grps || [])) {
      try {
        const { data } = await fetchClient.get(`/wms/metrics?grupoId=${grp.id}&year=${yr}`);
        allMetrics.push(...(data || []));
      } catch (e) {
        console.log(`Senior WMS grupo ${grp.id} sem dados para ${yr}`);
      }
    }
    const grouped = {};
    allMetrics.forEach((metric) => {
      const m = parseInt(metric.month);
      if (!grouped[m]) grouped[m] = { docCount: 0, totalValue: 0 };
      grouped[m].docCount += metric.transactionCount || 0;
      if (!metric.currency || metric.currency === "BRL")
        grouped[m].totalValue += metric.totalValue || 0;
    });
    return Object.entries(grouped).map(([month, d]) => ({
      source: "Senior", month: parseInt(month), docCount: d.docCount, totalValue: d.totalValue,
    }));
  };

  // E1 - External API
  const fetchE1Data = async (yr) => {
    const response = await axios.post(
      `${E1_API_URL}/document-statistics/summary`,
      { fromYear: yr, fromMonth: 1, toYear: yr, toMonth: 12 },
      { headers: { "Content-Type": "application/json", "x-api-key": E1_API_KEY } }
    );
    const items = response.data?.items || [];
    const normalized = [];
    const grouped = {};
    items.forEach((item) => {
      const key = item.month;
      if (!grouped[key]) grouped[key] = { docCount: 0, totalValue: 0 };
      grouped[key].docCount += item.docCount || 0;
      grouped[key].totalValue += item.totalValue || 0;
    });
    Object.entries(grouped).forEach(([month, data]) => {
      const [, m] = month.split("-");
      normalized.push({ source: "EssentialOne", month: parseInt(m), docCount: data.docCount, totalValue: data.totalValue });
    });
    return normalized;
  };

  // Comerzzia - Internal API
  const fetchComerzziaData = async (yr) => {
    const { data } = await fetchClient.get(`/comerzzia/invoice-metrics?year=${yr}&companyId=all`);
    const grouped = {};
    (data || []).forEach((metric) => {
      const m = parseInt(metric.month);
      if (!grouped[m]) grouped[m] = { docCount: 0, totalValue: 0 };
      grouped[m].docCount += metric.transactions || 0;
      grouped[m].totalValue += metric.totalValue || 0;
    });
    return Object.entries(grouped).map(([month, d]) => ({
      source: "Comerzzia", month: parseInt(month), docCount: d.docCount, totalValue: d.totalValue,
    }));
  };

  // Concur - Internal API
  const fetchConcurData = async (yr) => {
    // Load concurs first
    let allConcurs = [];
    let nextToken = null;
    do {
      const { data } = await fetchClient.get(`/getAllConcur?limit=100${nextToken ? `&nextToken=${nextToken}` : ""}`);
      allConcurs = [...allConcurs, ...data.value];
      nextToken = data.nextToken;
    } while (nextToken);

    // Fetch metrics for all concurs
    const allMetrics = [];
    for (const concur of allConcurs) {
      try {
        const { data } = await fetchClient.get(`/concur/expense-metrics?concur_id=${concur.id}&year=${yr}`);
        allMetrics.push(...(data || []));
      } catch (e) {
        console.log(`Concur ${concur.id} sem dados para ${yr}`);
      }
    }

    const grouped = {};
    allMetrics.forEach((metric) => {
      const m = parseInt(metric.month);
      if (!grouped[m]) grouped[m] = { docCount: 0, totalValue: 0 };
      grouped[m].docCount += metric.documentCount || 0;
      if (!metric.currency || metric.currency === "BRL")
        grouped[m].totalValue += metric.totalAmount || 0;
    });
    return Object.entries(grouped).map(([month, d]) => ({
      source: "Concur", month: parseInt(month), docCount: d.docCount, totalValue: d.totalValue,
    }));
  };

  // Viceri - Internal API
  const fetchViceriData = async (yr) => {
    const { data: apps } = await fetchClient.get("/viceri/aplicacao/getAllAplicacao");
    const allMetrics = [];

    for (const app of apps) {
      try {
        const { data } = await fetchClient.get(`/viceri/dashboard/${app.id}`);
        Object.keys(data).forEach((monthKey) => {
          const [keyYear, keyMonth] = monthKey.split("-");
          if (String(keyYear) === String(yr)) {
            const monthData = data[monthKey];
            let totalProcessed = 0;
            let totalValue = 0;
            ["purchase", "return", "downpayment"].forEach((type) => {
              if (monthData[type]) {
                totalProcessed += monthData[type].processed || 0;
                totalValue += monthData[type].totalValue || 0;
              }
            });
            allMetrics.push({ month: parseInt(keyMonth), docCount: totalProcessed, totalValue });
          }
        });
      } catch (e) {
        console.log(`Viceri app ${app.id} sem dados`);
      }
    }

    const grouped = {};
    allMetrics.forEach((m) => {
      if (!grouped[m.month]) grouped[m.month] = { docCount: 0, totalValue: 0 };
      grouped[m.month].docCount += m.docCount;
      grouped[m.month].totalValue += m.totalValue;
    });
    return Object.entries(grouped).map(([month, d]) => ({
      source: "Viceri", month: parseInt(month), docCount: d.docCount, totalValue: d.totalValue,
    }));
  };

  // Filter by selected modules
  const filteredSourceData = useMemo(() => {
    if (!sourceData) return null;
    if (selectedModules.length === ALL_MODULES.length) return sourceData;
    const filtered = {};
    Object.entries(sourceData).forEach(([source, items]) => {
      if (selectedModules.includes(source)) filtered[source] = items;
    });
    return filtered;
  }, [sourceData, selectedModules]);

  // Compute totals per source
  const sourceTotals = useMemo(() => {
    if (!filteredSourceData) return [];
    return Object.entries(filteredSourceData).map(([source, items]) => {
      const docCount = items.reduce((s, i) => s + i.docCount, 0);
      const totalValue = items.reduce((s, i) => s + i.totalValue, 0);
      return { source, docCount, totalValue, ticketMedio: docCount > 0 ? totalValue / docCount : 0 };
    });
  }, [filteredSourceData]);

  const grandTotals = useMemo(() => {
    const docCount = sourceTotals.reduce((s, t) => s + t.docCount, 0);
    const totalValue = sourceTotals.reduce((s, t) => s + t.totalValue, 0);
    const allItems = filteredSourceData ? Object.values(filteredSourceData).flat() : [];
    const uniqueMonths = new Set(allItems.map((i) => i.month));
    const monthCount = uniqueMonths.size || 1;
    return {
      docCount,
      totalValue,
      averagePerMonth: Math.round(docCount / monthCount),
      ticketMedio: docCount > 0 ? totalValue / docCount : 0,
    };
  }, [sourceTotals, filteredSourceData]);

  // Chart: Documentos por Módulo (Pie)
  const docsBySourceData = useMemo(() => {
    const chartData = [["Módulo", "Documentos"]];
    sourceTotals.filter((s) => s.docCount > 0).forEach((s) => chartData.push([s.source, s.docCount]));
    return chartData;
  }, [sourceTotals]);

  // Chart: Valor por Módulo (Bar)
  const valueBySourceData = useMemo(() => {
    const chartData = [["Módulo", "Valor (R$)"]];
    sourceTotals.filter((s) => s.totalValue > 0).sort((a, b) => b.totalValue - a.totalValue).forEach((s) => chartData.push([s.source, s.totalValue]));
    return chartData;
  }, [sourceTotals]);

  // Chart: Evolução Mensal Consolidada (Combo)
  const evolutionData = useMemo(() => {
    if (!filteredSourceData) return [["Mês", "Documentos", "Valor Total (R$)"]];
    const allItems = Object.values(filteredSourceData).flat();
    const grouped = {};
    allItems.forEach((item) => {
      if (!grouped[item.month]) grouped[item.month] = { docCount: 0, totalValue: 0 };
      grouped[item.month].docCount += item.docCount;
      grouped[item.month].totalValue += item.totalValue;
    });
    const chartData = [["Mês", "Documentos", "Valor Total (R$)"]];
    Object.keys(grouped)
      .map(Number)
      .sort((a, b) => a - b)
      .forEach((m) => chartData.push([MONTH_NAMES[m - 1], grouped[m].docCount, grouped[m].totalValue]));
    return chartData;
  }, [filteredSourceData]);

  // Chart: Comparativo Mensal por Módulo (Stacked Column)
  const compareMonthlyData = useMemo(() => {
    if (!filteredSourceData) return [["Mês", "EssentialOne", "Comerzzia", "Concur", "Viceri", "Ariba", "Senior"]];
    const sources = Object.keys(filteredSourceData);
    const monthlyBySource = {};
    sources.forEach((src) => {
      (filteredSourceData[src] || []).forEach((item) => {
        if (!monthlyBySource[item.month]) monthlyBySource[item.month] = {};
        monthlyBySource[item.month][src] = (monthlyBySource[item.month][src] || 0) + item.docCount;
      });
    });
    const chartData = [["Mês", ...sources]];
    Object.keys(monthlyBySource)
      .map(Number)
      .sort((a, b) => a - b)
      .forEach((m) => {
        const row = [MONTH_NAMES[m - 1]];
        sources.forEach((src) => row.push(monthlyBySource[m]?.[src] || 0));
        chartData.push(row);
      });
    return chartData;
  }, [filteredSourceData]);

  const docsBySourceOptions = {
    title: "Documentos por Módulo",
    chartArea: { width: "90%", height: "80%" },
    colors: PIE_COLORS,
    legend: { position: "right", textStyle: { fontSize: 13 } },
    pieHole: 0.4,
    animation: { startup: true, duration: 1000, easing: "out" },
  };

  const valueBySourceOptions = {
    title: "Valor por Módulo (R$)",
    chartArea: { width: "70%", height: "70%" },
    hAxis: { title: "Valor (R$)", format: "short" },
    colors: PIE_COLORS,
    legend: { position: "none" },
    animation: { startup: true, duration: 1000, easing: "out" },
  };

  const evolutionOptions = {
    title: `Evolução Mensal Consolidada - ${year}`,
    chartArea: { width: "75%", height: "70%" },
    hAxis: { title: "Mês" },
    seriesType: "line",
    series: {
      0: { color: "#2c3e50", targetAxisIndex: 0, lineWidth: 3, pointSize: 7 },
      1: { color: "#e67e22", targetAxisIndex: 1, lineWidth: 3, pointSize: 7 },
    },
    vAxes: {
      0: { title: "Documentos", gridlines: { color: "#f0f0f0" } },
      1: { title: "Valor Total (R$)", format: "short", gridlines: { color: "#f5f5f5" } },
    },
    legend: { position: "top" },
    animation: { startup: true, duration: 1000, easing: "out" },
  };

  const compareMonthlyOptions = {
    title: `Comparativo Mensal por Módulo - ${year}`,
    chartArea: { width: "75%", height: "70%" },
    hAxis: { title: "Mês" },
    vAxis: { title: "Documentos", minValue: 0 },
    colors: PIE_COLORS,
    isStacked: true,
    legend: { position: "top" },
    animation: { startup: true, duration: 1000, easing: "out" },
  };

  const kpiVariation = useMemo(() => {
    const pct = (curr, prev) => (prev > 0 ? ((curr - prev) / prev) * 100 : null);
    if (!filteredSourceData) return { docs: null, value: null, ticket: null };
    const allItems = Object.values(filteredSourceData).flat();
    if (!allItems.length) return { docs: null, value: null, ticket: null };
    const grouped = {};
    allItems.forEach((item) => {
      const m = item.month;
      if (!grouped[m]) grouped[m] = { docCount: 0, totalValue: 0 };
      grouped[m].docCount += item.docCount;
      grouped[m].totalValue += item.totalValue;
    });
    const months = Object.keys(grouped).map(Number).sort((a, b) => a - b);
    if (months.length < 2) return { docs: null, value: null, ticket: null };
    const last = grouped[months[months.length - 1]];
    const prev = grouped[months[months.length - 2]];
    const lastTicket = last.docCount > 0 ? last.totalValue / last.docCount : 0;
    const prevTicket = prev.docCount > 0 ? prev.totalValue / prev.docCount : 0;
    return {
      docs: pct(last.docCount, prev.docCount),
      value: pct(last.totalValue, prev.totalValue),
      ticket: pct(lastTicket, prevTicket),
    };
  }, [filteredSourceData]);

  const totalDocs = grandTotals.docCount;

  return (
    <Container>
      <ModernHeader>
        <HeaderLeft>
          <BackButton onClick={() => history.goBack()}>
            <ArrowBack />
          </BackButton>
          <HeaderContent>
            <PageTitle>
              <Assessment style={{ marginRight: "12px", fontSize: "32px" }} />
              Dashboard Consolidado
            </PageTitle>
            <PageSubtitle>Análise unificada de todos os módulos</PageSubtitle>
          </HeaderContent>
        </HeaderLeft>
      </ModernHeader>

      <ResultsCard>
        {/* Filtros */}
        <FiltersCard>
          <FilterRow>
            <FilterGroup>
              <FilterLabel>
                <ViewModule style={{ fontSize: "16px" }} />
                Módulos
              </FilterLabel>
              <ModuleChipsRow>
                <ModuleChip
                  active={selectedModules.length === ALL_MODULES.length}
                  color="#2c3e50"
                  onClick={toggleAll}
                >
                  Todos
                </ModuleChip>
                {ALL_MODULES.map((mod) => (
                  <ModuleChip
                    key={mod}
                    active={selectedModules.includes(mod)}
                    color={SOURCE_COLORS[mod]}
                    onClick={() => toggleModule(mod)}
                  >
                    {mod}
                  </ModuleChip>
                ))}
              </ModuleChipsRow>
            </FilterGroup>
            <FilterGroup>
              <FilterLabel>
                <CalendarToday style={{ fontSize: "16px" }} />
                Ano
              </FilterLabel>
              <FilterSelectWrapper>
                <FilterSelect value={year} onChange={(e) => setYear(Number(e.target.value))}>
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </FilterSelect>
              </FilterSelectWrapper>
            </FilterGroup>
          </FilterRow>
        </FiltersCard>

        {isLoading && <Loader />}

        {!isLoading && sourceData && (
          <>
            {/* Totalizadores */}
            <TotalizersGrid>
              <TotalizerCard color="#2c3e50">
                <TotalizerIcon color="#2c3e50">
                  <Receipt />
                </TotalizerIcon>
                <TotalizerContent>
                  <TotalizerValue>{formatAbbrValue(grandTotals.docCount)}</TotalizerValue>
                  <TotalizerLabel>Total de Documentos</TotalizerLabel>
                  {kpiVariation.docs !== null && (
                    <VariationBadge positive={kpiVariation.docs >= 0}>
                      {kpiVariation.docs >= 0 ? "▲" : "▼"} {Math.abs(kpiVariation.docs).toFixed(1)}% vs ant.
                    </VariationBadge>
                  )}
                </TotalizerContent>
              </TotalizerCard>

              <TotalizerCard color="#e67e22">
                <TotalizerIcon color="#e67e22">
                  <TrendingUp />
                </TotalizerIcon>
                <TotalizerContent>
                  <TotalizerValue>{formatAbbrValue(grandTotals.totalValue, true)}</TotalizerValue>
                  <TotalizerLabel>Valor Total</TotalizerLabel>
                  {kpiVariation.value !== null && (
                    <VariationBadge positive={kpiVariation.value >= 0}>
                      {kpiVariation.value >= 0 ? "▲" : "▼"} {Math.abs(kpiVariation.value).toFixed(1)}% vs ant.
                    </VariationBadge>
                  )}
                </TotalizerContent>
              </TotalizerCard>

              <TotalizerCard color="#27ae60">
                <TotalizerIcon color="#27ae60">
                  <CalendarToday />
                </TotalizerIcon>
                <TotalizerContent>
                  <TotalizerValue>{formatAbbrValue(grandTotals.averagePerMonth)}</TotalizerValue>
                  <TotalizerLabel>Média por Mês</TotalizerLabel>
                </TotalizerContent>
              </TotalizerCard>

              <TotalizerCard color="#8e44ad">
                <TotalizerIcon color="#8e44ad">
                  <Assessment />
                </TotalizerIcon>
                <TotalizerContent>
                  <TotalizerValue>{formatAbbrValue(grandTotals.ticketMedio, true)}</TotalizerValue>
                  <TotalizerLabel>Ticket Médio</TotalizerLabel>
                  {kpiVariation.ticket !== null && (
                    <VariationBadge positive={kpiVariation.ticket >= 0}>
                      {kpiVariation.ticket >= 0 ? "▲" : "▼"} {Math.abs(kpiVariation.ticket).toFixed(1)}% vs ant.
                    </VariationBadge>
                  )}
                </TotalizerContent>
              </TotalizerCard>
            </TotalizersGrid>

            {/* Source indicator badges */}
            <SourceBadgesRow>
              {sourceTotals.map((s) => (
                <SourceBadge key={s.source} color={SOURCE_COLORS[s.source]}>
                  <SourceDot color={SOURCE_COLORS[s.source]} />
                  <SourceName>{s.source}</SourceName>
                  <SourceCount>{formatAbbrValue(s.docCount)} docs</SourceCount>
                </SourceBadge>
              ))}
            </SourceBadgesRow>

            {grandTotals.docCount > 0 ? (
              <>
                {/* Evolução mensal consolidada */}
                <ChartCard>
                  <Chart
                    chartType="ComboChart"
                    width="100%"
                    height="420px"
                    data={evolutionData}
                    options={evolutionOptions}
                  />
                </ChartCard>

                {/* Comparativo por módulo */}
                <ChartCard>
                  <Chart
                    chartType="ColumnChart"
                    width="100%"
                    height="400px"
                    data={compareMonthlyData}
                    options={compareMonthlyOptions}
                  />
                </ChartCard>

                {/* Pie + Bar side by side */}
                <ChartsRow>
                  {docsBySourceData.length > 1 && (
                    <ChartCard>
                      <Chart
                        chartType="PieChart"
                        width="100%"
                        height="360px"
                        data={docsBySourceData}
                        options={docsBySourceOptions}
                      />
                    </ChartCard>
                  )}
                  {valueBySourceData.length > 1 && (
                    <ChartCard>
                      <Chart
                        chartType="BarChart"
                        width="100%"
                        height="360px"
                        data={valueBySourceData}
                        options={valueBySourceOptions}
                      />
                    </ChartCard>
                  )}
                </ChartsRow>

                {/* Tabela por módulo */}
                <TableCard>
                  <TableTitle>Detalhamento por Módulo</TableTitle>
                  <TableWrap>
                    <DataTable>
                      <thead>
                        <tr>
                          <TableHeader>Módulo</TableHeader>
                          <TableHeader align="right">Documentos</TableHeader>
                          <TableHeader align="right">Valor Total</TableHeader>
                          <TableHeader align="right">Ticket Médio</TableHeader>
                          <TableHeader align="right">% do Total</TableHeader>
                        </tr>
                      </thead>
                      <tbody>
                        {sourceTotals
                          .sort((a, b) => b.docCount - a.docCount)
                          .map((s) => (
                            <TRow key={s.source}>
                              <TCell>
                                <ModuleBadge color={SOURCE_COLORS[s.source]}>
                                  {s.source}
                                </ModuleBadge>
                              </TCell>
                              <TCell align="right">
                                <MetricBadge color={SOURCE_COLORS[s.source]}>
                                  {s.docCount.toLocaleString("pt-BR")}
                                </MetricBadge>
                              </TCell>
                              <TCell align="right">
                                <MetricBadge color="#e67e22">
                                  {s.totalValue.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                                </MetricBadge>
                              </TCell>
                              <TCell align="right">
                                <MetricBadge color="#27ae60">
                                  {s.ticketMedio.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                                </MetricBadge>
                              </TCell>
                              <TCell align="right">
                                <MetricBadge color="#2c3e50">
                                  {totalDocs > 0 ? ((s.docCount / totalDocs) * 100).toFixed(1) : "0"}%
                                </MetricBadge>
                              </TCell>
                            </TRow>
                          ))}
                        <TRowTotal>
                          <TCell><strong>Total</strong></TCell>
                          <TCell align="right">
                            <strong>{grandTotals.docCount.toLocaleString("pt-BR")}</strong>
                          </TCell>
                          <TCell align="right">
                            <strong>{grandTotals.totalValue.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</strong>
                          </TCell>
                          <TCell align="right">
                            <strong>{grandTotals.ticketMedio.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</strong>
                          </TCell>
                          <TCell align="right">
                            <strong>100%</strong>
                          </TCell>
                        </TRowTotal>
                      </tbody>
                    </DataTable>
                  </TableWrap>
                </TableCard>
              </>
            ) : (
              <EmptyCard>
                <Assessment style={{ fontSize: "64px", color: "#cbd5e1", marginBottom: "16px" }} />
                <EmptyTitle>Nenhum dado encontrado</EmptyTitle>
                <EmptyText>Não há documentos para o ano selecionado em nenhum módulo.</EmptyText>
              </EmptyCard>
            )}
          </>
        )}

        {!isLoading && !sourceData && (
          <EmptyCard>
            <Assessment style={{ fontSize: "64px", color: "#cbd5e1", marginBottom: "16px" }} />
            <EmptyTitle>Carregando dados...</EmptyTitle>
            <EmptyText>Aguarde enquanto os dados são carregados de todos os módulos.</EmptyText>
          </EmptyCard>
        )}
      </ResultsCard>
    </Container>
  );
}

export default DashboardConsolidado;

// ===== STYLED COMPONENTS =====

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const BackButton = styled.button`
  background: rgba(52, 152, 219, 0.1);
  border: none;
  border-radius: 12px;
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #3498db;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(52, 152, 219, 0.2);
    transform: translateX(-4px);
  }
`;

const FiltersCard = styled.div`
  padding: 24px 0;
  margin-bottom: 24px;
  border-bottom: 1px solid #f0f0f0;
`;


const FilterRow = styled.div`
  display: flex;
  gap: 24px;
  align-items: end;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ModuleChipsRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const ModuleChip = styled.button`
  border: 2px solid ${(props) => (props.active ? props.color : "#e5e7eb")};
  border-radius: 10px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  font-family: "Mulish", sans-serif;
  background: ${(props) => (props.active ? `${props.color}15` : "#f9fafb")};
  color: ${(props) => (props.active ? props.color : "#9ca3af")};
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    border-color: ${(props) => props.color};
    background: ${(props) => `${props.color}10`};
    color: ${(props) => props.color};
  }

  &::before {
    content: "";
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${(props) => (props.active ? props.color : "#d1d5db")};
    transition: all 0.2s ease;
  }
`;

const FilterLabel = styled.label`
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  font-family: "Mulish", sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const FilterSelectWrapper = styled.div`
  position: relative;
  &::after {
    content: "";
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid #9ca3af;
    pointer-events: none;
  }
`;

const FilterSelect = styled.select`
  width: 100%;
  padding: 11px 32px 11px 14px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  font-family: "Mulish", sans-serif;
  background: #f9fafb;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #2c3e50;
  appearance: none;

  &:focus {
    outline: none;
    border-color: #3498db;
    box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
    background: white;
  }

  &:hover {
    border-color: #d0d0d0;
    background: white;
  }
`;


const TotalizersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
`;

const TotalizerCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid #e8e8e8;
  display: flex;
  align-items: center;
  gap: 16px;
  animation: ${fadeIn} 0.5s ease;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  }
`;

const TotalizerIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: ${(props) => `${props.color}15`};
  color: ${(props) => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    font-size: 28px;
  }
`;

const TotalizerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TotalizerValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  font-family: "Mulish", sans-serif;
`;

const TotalizerLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  font-family: "Mulish", sans-serif;
`;

const VariationBadge = styled.span`
  display: inline-block;
  margin-top: 6px;
  padding: 3px 9px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  font-family: "Mulish", sans-serif;
  background: ${(props) => (props.positive ? "#dcfce7" : "#fee2e2")};
  color: ${(props) => (props.positive ? "#15803d" : "#dc2626")};
`;

// Source badges
const SourceBadgesRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const SourceBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 12px;
  background: ${(props) => `${props.color}10`};
  border: 1px solid ${(props) => `${props.color}30`};
  animation: ${fadeIn} 0.5s ease;
`;

const SourceDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${(props) => props.color};
`;

const SourceName = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #2c3e50;
  font-family: "Mulish", sans-serif;
`;

const SourceCount = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  font-family: "Mulish", sans-serif;
`;

// Charts
const ChartsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 24px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }

  & > div {
    margin-bottom: 0;
  }
`;

const ChartCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid #e8e8e8;
  margin-bottom: 24px;
  animation: ${fadeIn} 0.5s ease;
`;

// Table
const TableCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid #e8e8e8;
  animation: ${fadeIn} 0.5s ease;
`;

const TableTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 20px 0;
  font-family: "Mulish", sans-serif;
`;

const TableWrap = styled.div`
  overflow-x: auto;
`;

const DataTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.th`
  text-align: ${(props) => props.align || "left"};
  padding: 12px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #e5e7eb;
  font-family: "Mulish", sans-serif;
`;

const TRow = styled.tr`
  border-bottom: 1px solid #e5e7eb;
  transition: background 0.2s ease;

  &:hover {
    background: #f8f9fa;
  }
`;

const TRowTotal = styled.tr`
  border-top: 2px solid #2c3e50;
  background: #f8f9fa;

  td {
    font-size: 15px;
  }
`;

const TCell = styled.td`
  text-align: ${(props) => props.align || "left"};
  padding: 16px;
  font-size: 14px;
  color: #2c3e50;
  font-family: "Mulish", sans-serif;
`;

const MetricBadge = styled.span`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  background: ${(props) => `${props.color}15`};
  color: ${(props) => props.color};
`;

const ModuleBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  background: ${(props) => `${props.color}15`};
  color: ${(props) => props.color};

  &::before {
    content: "";
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${(props) => props.color};
  }
`;

// Empty
const EmptyCard = styled.div`
  text-align: center;
  padding: 64px 24px;
  animation: ${fadeIn} 0.5s ease;
`;

const EmptyTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 12px 0;
  font-family: "Mulish", sans-serif;
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  font-family: "Mulish", sans-serif;
`;
